import { memo } from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import type { NumberMode } from '../types';

export interface NumberSetBadgeProps {
  /** Number mode to display */
  numberMode: NumberMode | undefined;
  /** Size of the badge */
  size?: ChipProps['size'];
  /** Variant style */
  variant?: ChipProps['variant'];
  /** Custom tooltip text */
  tooltip?: string;
  /** Don't show badge for single mode (default behavior) */
  hideForSingle?: boolean;
}

/**
 * Get display label for number mode
 */
function getLabelForMode(mode: NumberMode | undefined): string {
  switch (mode) {
    case 'half':
      return '2 Sets';
    case 'quarter':
      return '3-4 Sets';
    case 'single':
    default:
      return '1 Set';
  }
}

/**
 * Get tooltip description for number mode
 */
function getTooltipForMode(mode: NumberMode | undefined): string {
  switch (mode) {
    case 'half':
      return 'Different numbers per half';
    case 'quarter':
      return 'Different numbers per quarter';
    case 'single':
    default:
      return 'Same numbers for all periods';
  }
}

/**
 * Get color based on number mode
 * - single: default (gray) - hidden by default
 * - half: primary (blue)
 * - quarter: secondary (purple)
 */
function getColorForMode(mode: NumberMode | undefined): ChipProps['color'] {
  switch (mode) {
    case 'quarter':
      return 'secondary';
    case 'half':
      return 'primary';
    case 'single':
    default:
      return 'default';
  }
}

/**
 * Badge showing the number set type for a board.
 * Used on BoardCard to indicate premium boards with multiple number sets.
 * By default, hides badge for single-set boards (the standard tier).
 */
export const NumberSetBadge = memo<NumberSetBadgeProps>(({
  numberMode,
  size = 'small',
  variant = 'filled',
  tooltip,
  hideForSingle = true,
}) => {
  // Don't render for single mode if hideForSingle is true
  if (hideForSingle && (!numberMode || numberMode === 'single')) {
    return null;
  }

  const label = getLabelForMode(numberMode);
  const color = getColorForMode(numberMode);
  const tooltipText = tooltip ?? getTooltipForMode(numberMode);

  const chip = (
    <Chip
      size={size}
      variant={variant}
      label={label}
      color={color}
    />
  );

  if (tooltip === '') {
    return chip;
  }

  return (
    <Tooltip title={tooltipText}>
      {chip}
    </Tooltip>
  );
});

NumberSetBadge.displayName = 'NumberSetBadge';

export default NumberSetBadge;
